const DEFAULT_MIN_DRAG_PIXELS = 10;

function toNumber(value) {
    return typeof value === 'function' ? value() : value;
}

/**
 * Build a closed rectangle path plus its NE/SW corners from two lat/lng points.
 * Normalizes drag direction so the corners are correct however the user drags.
 */
export function buildRectangleFromLatLngs(startLatLng, endLatLng) {
    const startLat = toNumber(startLatLng.lat);
    const startLng = toNumber(startLatLng.lng);
    const endLat = toNumber(endLatLng.lat);
    const endLng = toNumber(endLatLng.lng);

    const north = Math.max(startLat, endLat);
    const south = Math.min(startLat, endLat);
    const east = Math.max(startLng, endLng);
    const west = Math.min(startLng, endLng);

    const nw = { lat: north, lng: west };
    const ne = { lat: north, lng: east };
    const se = { lat: south, lng: east };
    const sw = { lat: south, lng: west };

    return { path: [nw, ne, se, sw, nw], ne, sw };
}

function pointerOffset(event, mapDiv) {
    const source = event.touches?.[0] || event.changedTouches?.[0] || event;
    const rect = mapDiv.getBoundingClientRect();
    return { x: source.clientX - rect.left, y: source.clientY - rect.top };
}

/**
 * Drag-to-draw rectangle on a Google map, replacing the removed drawing library.
 * Gestures are disabled for the lifetime of the controller so a drag draws
 * instead of panning.
 *
 * @returns {{ destroy: Function }}
 */
export function attachRectangleDraw({ map, api, onPreview, onComplete, minDragPixels = DEFAULT_MIN_DRAG_PIXELS }) {
    const mapDiv = map.getDiv();
    const restoreOptions = {
        gestureHandling: map.get('gestureHandling'),
        draggableCursor: map.get('draggableCursor'),
    };
    map.setOptions({ gestureHandling: 'none', draggableCursor: 'crosshair' });

    // An OverlayView is the only way to convert container pixels back to lat/lng.
    const overlay = new api.OverlayView();
    overlay.onAdd = () => {};
    overlay.draw = () => {};
    overlay.setMap(map);

    let startPoint = null;
    let currentPoint = null;

    function toLatLng(point) {
        return overlay.getProjection()?.fromContainerPixelToLatLng(new api.Point(point.x, point.y));
    }

    function onStart(event) {
        // Map controls (cancel, zoom) are rendered inside the map div. Starting a
        // drag there would preventDefault the move and swallow the tap.
        if (event.target?.closest?.('button, a, input, select, textarea, label')) {
            return;
        }
        startPoint = pointerOffset(event, mapDiv);
        currentPoint = startPoint;
    }

    function onMove(event) {
        if (!startPoint) {
            return;
        }
        event.preventDefault();
        currentPoint = pointerOffset(event, mapDiv);

        const start = toLatLng(startPoint);
        const end = toLatLng(currentPoint);
        if (start && end) {
            onPreview?.(buildRectangleFromLatLngs(start, end).path);
        }
    }

    function onEnd() {
        if (!startPoint) {
            return;
        }
        const dragDistance = Math.hypot(currentPoint.x - startPoint.x, currentPoint.y - startPoint.y);
        const start = toLatLng(startPoint);
        const end = toLatLng(currentPoint);

        startPoint = null;
        currentPoint = null;
        onPreview?.([]);

        if (dragDistance < minDragPixels || !start || !end) {
            return;
        }

        const { path, ne, sw } = buildRectangleFromLatLngs(start, end);
        const bounds = new api.LatLngBounds(new api.LatLng(sw.lat, sw.lng), new api.LatLng(ne.lat, ne.lng));
        onComplete?.({ bounds, path });
    }

    const bindings = [
        [mapDiv, 'mousedown', onStart],
        [mapDiv, 'touchstart', onStart],
        [document, 'mousemove', onMove],
        [mapDiv, 'touchmove', onMove],
        [document, 'mouseup', onEnd],
        [document, 'touchend', onEnd],
    ];
    bindings.forEach(([target, type, handler]) => target.addEventListener(type, handler, { passive: false }));

    return {
        destroy() {
            bindings.forEach(([target, type, handler]) => target.removeEventListener(type, handler));
            overlay.setMap(null);
            map.setOptions(restoreOptions);
            onPreview?.([]);
        },
    };
}
